// client/src/hooks/useVitals.js
// Итерация 5: хукс витальности персонажа.
// Подписывается на WS-события health_update/energy_update/hunger_update/thirst_update/character_update
// и аккумулирует их в единое состояние для VitalsBlock.

import { useState, useEffect, useCallback, useRef } from 'react';

const DEFAULT_VITALS = {
  health: { current: 100, max: 100, wounds: 0, regen: 0, regen_rate: 5 },
  energy: { current: 100, max: 100, fatigue: 0, rest_rate: 3 },
  hunger: { current: 0, max: 100, rate: -0.5 },
  thirst: { current: 0, max: 100, rate: -0.8 },
};

const STALE_THRESHOLD_MS = 8000;

const isDefaultVitals = (v) => {
  if (!v) return true;
  const h = v.health?.current;
  const e = v.energy?.current;
  const hu = v.hunger?.current;
  const t = v.thirst?.current;
  return h === 100 && e === 100 && hu === 0 && t === 0;
};

const normalizeVital = (incoming, fallback) => {
  if (!incoming || typeof incoming !== 'object') return fallback;
  return {
    current: typeof incoming.current === 'number' ? incoming.current : fallback.current,
    max: typeof incoming.max === 'number' ? incoming.max : fallback.max,
    ...incoming,
  };
};

export default function useVitals(channel, httpVitals) {
  const [vitals, setVitals] = useState(() => ({
    ...DEFAULT_VITALS,
    ...(httpVitals || {}),
  }));
  const [isLive, setIsLive] = useState(false);
  const [isStale, setIsStale] = useState(true);
  const [characterMeta, setCharacterMeta] = useState({
    level: 1,
    in_structure: false,
    deck_id: null,
    in_transit: false,
    surface: false,
  });

  const lastEventRef = useRef(0);
  const staleTimerRef = useRef(null);

  useEffect(() => {
    if (!httpVitals) return;
    if (!isLive) {
      setVitals((prev) => ({
        ...prev,
        ...httpVitals,
      }));
      if (!isDefaultVitals(httpVitals)) {
        setIsStale(false);
      }
    }
  }, [httpVitals, isLive]);

  useEffect(() => {
    if (!channel) return;

    const markLive = () => {
      lastEventRef.current = Date.now();
      setIsLive(true);
      setIsStale(false);
      if (staleTimerRef.current) {
        clearTimeout(staleTimerRef.current);
        staleTimerRef.current = null;
      }
    };

    const handleHealth = (payload) => {
      if (!payload?.health) return;
      markLive();
      setVitals((prev) => ({
        ...prev,
        health: { ...prev.health, ...payload.health },
      }));
    };

    const handleEnergy = (payload) => {
      if (!payload?.energy) return;
      markLive();
      setVitals((prev) => ({
        ...prev,
        energy: { ...prev.energy, ...payload.energy },
      }));
    };

    const handleHunger = (payload) => {
      if (!payload?.hunger) return;
      markLive();
      setVitals((prev) => ({
        ...prev,
        hunger: { ...prev.hunger, ...payload.hunger },
      }));
    };

    const handleThirst = (payload) => {
      if (!payload?.thirst) return;
      markLive();
      setVitals((prev) => ({
        ...prev,
        thirst: { ...prev.thirst, ...payload.thirst },
      }));
    };

    const handleCharacter = (payload) => {
      if (!payload?.state) return;
      markLive();
      setCharacterMeta((prev) => ({
        ...prev,
        level: payload.state.level ?? prev.level,
        in_structure: payload.state.in_structure ?? prev.in_structure,
        deck_id: payload.state.deck_id ?? prev.deck_id,
        in_transit: payload.state.in_transit ?? prev.in_transit,
        surface: payload.state.surface ?? prev.surface,
      }));
    };

    const handleGameState = (payload) => {
      if (!payload?.state) return;
      markLive();
      const s = payload.state;
      setVitals({
        health: normalizeVital(s.health, DEFAULT_VITALS.health),
        energy: normalizeVital(s.energy, DEFAULT_VITALS.energy),
        hunger: normalizeVital(s.hunger, DEFAULT_VITALS.hunger),
        thirst: normalizeVital(s.thirst, DEFAULT_VITALS.thirst),
      });
      setCharacterMeta({
        level: s.level ?? 1,
        in_structure: !!s.in_structure,
        deck_id: s.deck_id ?? null,
        in_transit: !!s.in_transit,
        surface: !!s.surface,
      });
    };

    channel.on('health_update', handleHealth);
    channel.on('energy_update', handleEnergy);
    channel.on('hunger_update', handleHunger);
    channel.on('thirst_update', handleThirst);
    channel.on('character_update', handleCharacter);
    channel.on('game_state', handleGameState);

    try {
      channel.push('get_game_state', {});
    } catch (err) {
      // канал может быть ещё не готов
    }

    staleTimerRef.current = setTimeout(() => {
      if (Date.now() - lastEventRef.current >= STALE_THRESHOLD_MS) {
        setIsStale(true);
      }
    }, STALE_THRESHOLD_MS);

    return () => {
      channel.off('health_update', handleHealth);
      channel.off('energy_update', handleEnergy);
      channel.off('hunger_update', handleHunger);
      channel.off('thirst_update', handleThirst);
      channel.off('character_update', handleCharacter);
      channel.off('game_state', handleGameState);
      if (staleTimerRef.current) {
        clearTimeout(staleTimerRef.current);
        staleTimerRef.current = null;
      }
    };
  }, [channel]);

  const refetch = useCallback(() => {
    if (!channel) return;
    try {
      channel.push('get_game_state', {});
    } catch (err) {
      // ignore
    }
  }, [channel]);

  return { vitals, characterMeta, isLive, isStale, refetch };
}
